import type { components } from "@/api-schema";
import { mapAttributeValueToName } from "@/common/helpers/map-attribute-value-to-name";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonVariant, ButtonSize } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type AppRole = components["schemas"]["AppRole"];
type AppRoleAttribute = components["schemas"]["AppRoleAttribute"];

const MAX_VISIBLE_ROLES = 10;

export interface RoleSectionProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  roles: AppRole[];
  attributes: AppRoleAttribute[];
}

export function RoleSection({ icon: Icon, title, roles, attributes }: RoleSectionProps) {
  const { t } = useTranslation(["ram", "common"]);
  const [isExpanded, setIsExpanded] = useState(false);

  const totalFiltered = roles.length + attributes.length;

  const shouldShowExpandButton = totalFiltered > MAX_VISIBLE_ROLES;
  const displayLimit = shouldShowExpandButton && !isExpanded ? MAX_VISIBLE_ROLES : totalFiltered;

  const displayedRoles = roles.slice(0, displayLimit);
  const remainingSlots = Math.max(0, displayLimit - roles.length);
  const displayedAttributes = attributes.slice(0, remainingSlots);
  const remainingCount = totalFiltered - displayLimit;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="h-4 w-4" />
          {title}
        </h4>
        {shouldShowExpandButton && (
          <Button
            variant={ButtonVariant.Ghost}
            size={ButtonSize.Sm}
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-6 w-6 p-0"
            aria-label={isExpanded ? t("common:Collapse") : t("common:Expand")}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-1">
        {displayedRoles.map((role) => (
          <Badge key={role.uuid} variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Secondary}>
            {role.name} {role.v1Id && `(V1: ${role.v1Id})`}
          </Badge>
        ))}
        {displayedAttributes.map((attribute) => (
          <Badge
            key={attribute.uuid}
            variant={BadgeVariant.Outlined}
            colorVariant={BadgeColorVariant.Secondary}
            tooltip={t("ram:AttributeAppRoleToolTip")}
          >
            {mapAttributeValueToName(attribute.type) || attribute.type}
          </Badge>
        ))}
        {shouldShowExpandButton && !isExpanded && remainingCount > 0 && (
          <Badge
            variant={BadgeVariant.Outlined}
            colorVariant={BadgeColorVariant.Secondary}
            onClick={() => setIsExpanded(true)}
          >
            +{remainingCount} {t("common:More")}
          </Badge>
        )}
      </div>
    </div>
  );
}
